import { StyleSheet, Text, View } from 'react-native';

import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { getContrastTextColor } from '@/lib/utils/color-contrast';
import type { FamilyMember } from '@/types/family-member';
import type { RoutePassengerChanges } from '@/types/route-plan';

type PassengerChipsProps = {
  passengerChanges: RoutePassengerChanges;
  familyMembers: FamilyMember[];
};

/**
 * PassengerChips — who joins (+Coco) or leaves (−Gummy) the route at a stop.
 */
export function PassengerChips({ passengerChanges, familyMembers }: PassengerChipsProps) {
  const membersById = new Map(familyMembers.map((member) => [member.id, member]));

  const chips = [
    ...passengerChanges.dropOff.map((memberId) => ({ memberId, sign: '−' })),
    ...passengerChanges.pickUp.map((memberId) => ({ memberId, sign: '+' })),
  ]
    .map(({ memberId, sign }) => {
      const member = membersById.get(memberId);
      return member ? { key: `${sign}${memberId}`, sign, member } : null;
    })
    .filter((chip): chip is { key: string; sign: string; member: FamilyMember } => chip !== null);

  if (chips.length === 0) return null;

  return (
    <View style={styles.container}>
      {chips.map((chip) => (
        <View key={chip.key} style={[styles.chip, { backgroundColor: chip.member.color }]}>
          <Text
            style={[
              styles.chipLabel,
              { color: getContrastTextColor(chip.member.color), fontFamily: getBodyFont() },
            ]}
          >
            {chip.sign}
            {chip.member.name}
          </Text>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.xs,
  },
  chip: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: 2,
    borderRadius: BorderRadius.xxl,
  },
  chipLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
});
